import {User} from '@common/auth/user';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {Trans} from '@common/i18n/trans';
import {Chip} from '@common/ui/forms/input-field/chip-field/chip';
import {FormattedDate} from '@common/i18n/formatted-date';
import {FormattedPrice} from '@common/i18n/formatted-price';
import {Button} from '@common/ui/buttons/button';
import {Link} from 'react-router-dom';
import {useProducts} from '@common/billing/pricing-table/use-products';

interface Props {
  user: User;
}

export function CurrentSubscriptionPanel({user}: Props) {
  const subscription = user.subscriptions?.[0];
  const productsQuery = useProducts('pricingPage');

  if (!subscription) {
    return (
      <AccountSettingsPanel
        id={AccountSettingsId.Subscription}
        title={<Trans message="Subscription" />}
      >
        <div className="flex items-start justify-between gap-20">
          <div>
            <div className="mb-6 text-lg font-semibold">
              <Trans message="No active package" />
            </div>
            <div className="text-sm text-muted">
              <Trans message="You are not subscribed to any package right now." />
            </div>
          </div>
          <Button
            variant="flat"
            color="primary"
            size="sm"
            elementType={Link}
            to="/pricing"
          >
            <Trans message="Upgrade" />
          </Button>
        </div>
      </AccountSettingsPanel>
    );
  }

  const status = subscription.active || subscription.valid ? 'active' : 'inactive';
  const renewalDate = subscription.renews_at || subscription.ends_at;
  const currentAmount = subscription.price?.amount ?? 0;
  const hasHigherPackage = !!productsQuery.data?.products
    .filter(product => !product.hidden && product.id !== subscription.product_id)
    .some(product =>
      product.prices.some(price => (price.amount ?? 0) > currentAmount),
    );

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.Subscription}
      title={<Trans message="Subscription" />}
    >
      <div className="flex items-start justify-between gap-20">
        <div>
          <Chip
            className="mb-10 w-min"
            size="xs"
            radius="rounded"
            color={status === 'active' ? 'positive' : 'danger'}
          >
            <Trans message={status === 'active' ? 'Active' : 'Expired'} />
          </Chip>
          <div className="mb-4 text-lg font-semibold">
            {subscription.product?.name || subscription.description}
          </div>
          {subscription.price ? (
            <FormattedPrice className="mb-8 text-base" price={subscription.price} />
          ) : null}
          <div className="text-sm text-muted">
            <Trans
              message="Current package: :name"
              values={{name: subscription.product?.name || subscription.description}}
            />
          </div>
          {renewalDate ? (
            <div className="mt-4 text-sm text-muted">
              <Trans
                message={
                  subscription.active || subscription.valid
                    ? 'Time to renew: :date'
                    : 'Expired on: :date'
                }
                values={{
                  date: <FormattedDate preset="long" date={renewalDate} />,
                }}
              />
            </div>
          ) : null}
        </div>
        {hasHigherPackage ? (
          <Button
            variant="flat"
            color="primary"
            size="sm"
            elementType={Link}
            to="/pricing"
          >
            <Trans message="Upgrade" />
          </Button>
        ) : null}
      </div>
    </AccountSettingsPanel>
  );
}
